import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import RecordingChoiceModal from '@renderer/components/bahela/RecordingChoiceModal'
import BahelaPayment from '../bahela/Payment'
import { normalizeCurrency, roundMoney } from '@renderer/utils/formatCurrency'

/**
 * Kotak payment: Foto & Rekam modal ONLY, then QRIS. No strip checkout UI (4R, no cut).
 * Voucher / free sessions (amount 0 or already settled) skip QR and continue the flow.
 */
const KotakPaymentPage: React.FC = () => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, logActivity, userPreferences } = useSessionStateStore()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const checkoutConfig = globalConfig?.pages.checkout
  const welcomeSettings = getPageSettings('welcome')
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  const customFrameSur = userPreferences.customFrameSurchargePerPackage ?? 0
  const recordingLocked = userPreferences.customFrameRecordingLocked === true

  const handledRef = useRef(false)
  const [showRecordingModal, setShowRecordingModal] = useState(false)
  const [paymentReady, setPaymentReady] = useState(false)

  const recordingPackageTypes = [
    'recording',
    'strip_live',
    'strip_story',
    'session_recording'
  ] as const

  const recordingPackages = (checkoutConfig?.settings.packages ?? []).filter((p) =>
    recordingPackageTypes.includes(p.type as (typeof recordingPackageTypes)[number])
  )
  const hasRecordingOption = recordingPackages.length > 0
  const captureRecordingEnabled = globalConfig?.pages?.capture?.settings?.recordingEnabled
  const recordingAvailable =
    captureRecordingEnabled !== false &&
    (captureRecordingEnabled === true || hasRecordingOption)

  const recordingPackage =
    recordingPackages[0] ||
    ({
      type: 'recording',
      price: 5000,
      optional: true
    } as { type: string; price: number; optional: boolean })

  const printPackage =
    checkoutConfig?.settings.packages?.find((p) => p.type === 'print' && (p.quantity ?? 1) === 1) ||
    checkoutConfig?.settings.packages?.find((p) => p.type === 'print') || {
      type: 'print',
      quantity: 1,
      price: 35000
    }

  const recordingPrice = recordingLocked ? 0 : recordingPackage.price

  const nextAfterPayment = (): string =>
    welcomeSettings?.startWithPayment ? 'filter' : 'capture'

  const skipToSession = useCallback(
    (reason: string): void => {
      const live = useSessionStateStore.getState().transaction
      setSessionState({
        transaction: {
          status: 'settlement',
          amount: live.amount ?? 0
        }
      })
      logActivity('Kotak payment skipped — free or voucher session', {
        reason,
        amount: live.amount ?? 0,
        discount: live.discount ?? 0,
        voucherCode: live.voucherCode
      })
      setShowRecordingModal(false)
      setPaymentReady(false)
      navigate(`/${projectId}/${nextAfterPayment()}`)
    },
    [logActivity, navigate, projectId, setSessionState, welcomeSettings?.startWithPayment]
  )

  const applyKotakPackage = useCallback(
    (includeRecording: boolean): number => {
      const liveTx = useSessionStateStore.getState().transaction
      const discountValue = liveTx.discount || 0
      const calculateDiscount = (basePrice: number): number => {
        if (discountValue < 0) {
          return roundMoney((basePrice * Math.abs(discountValue)) / 100, projectCurrency)
        }
        return discountValue
      }

      const effectiveRecordingPrice = includeRecording ? recordingPrice : 0
      const base = printPackage.price + customFrameSur + effectiveRecordingPrice
      const finalDiscount = calculateDiscount(base)
      const amount = roundMoney(Math.max(0, base - finalDiscount), projectCurrency)

      const prefs = useSessionStateStore.getState().userPreferences
      const derivedRecordingType =
        prefs.recordingType ??
        (recordingPackage.type === 'recording'
          ? 'strip_live'
          : (recordingPackage.type as 'strip_live' | 'strip_story' | 'session_recording'))

      setSessionState({
        userPreferences: {
          copies: printPackage.quantity ?? 1,
          useRecording: includeRecording,
          ...(includeRecording && derivedRecordingType
            ? { recordingType: derivedRecordingType }
            : { recordingType: null })
        },
        transaction: {
          amount,
          discount: finalDiscount,
          selectedPackages: [
            {
              type: 'print',
              quantity: printPackage.quantity ?? 1,
              price: printPackage.price
            },
            ...(includeRecording ? [recordingPackage] : [])
          ]
        }
      })

      logActivity('Kotak recording choice applied before payment', {
        amount,
        copies: printPackage.quantity ?? 1,
        useRecording: includeRecording,
        recordingPrice: effectiveRecordingPrice
      })

      return amount
    },
    [
      customFrameSur,
      logActivity,
      printPackage,
      projectCurrency,
      recordingPackage,
      recordingPrice,
      setSessionState
    ]
  )

  const finishRecordingChoice = useCallback(
    (includeRecording: boolean): void => {
      const amount = applyKotakPackage(includeRecording)
      setShowRecordingModal(false)

      const tx = useSessionStateStore.getState().transaction
      // Cash voucher (settled on Welcome) or discount that zeros the cart — no QRIS.
      if (tx.status === 'settlement' || amount <= 0) {
        skipToSession(tx.status === 'settlement' ? 'already_settled' : 'zero_amount_after_voucher')
        return
      }

      setPaymentReady(true)
    },
    [applyKotakPackage, skipToSession]
  )

  useEffect(() => {
    if (handledRef.current) return
    handledRef.current = true

    // Cash voucher already settled: still stamp packages, then skip QR.
    if (useSessionStateStore.getState().transaction.status === 'settlement') {
      finishRecordingChoice(recordingLocked || recordingAvailable)
      return
    }

    if (recordingLocked) {
      finishRecordingChoice(true)
      return
    }

    if (!recordingAvailable) {
      finishRecordingChoice(false)
      return
    }

    setShowRecordingModal(true)
  }, [recordingLocked, recordingAvailable, finishRecordingChoice])

  return (
    <>
      <RecordingChoiceModal
        isVisible={showRecordingModal}
        recordingPrice={recordingPrice}
        currency={projectCurrency}
        onChoice={finishRecordingChoice}
        language={i18n.language === 'id' ? 'id' : 'en'}
      />
      {paymentReady && <BahelaPayment />}
    </>
  )
}

export default KotakPaymentPage
